import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  PlusCircle,
  AlertCircle,
  CheckCircle2,
  FileCheck,
  Trash2,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
} from "lucide-react";
import { statusConfig } from "@/utils/constants";
import { usePagination } from "@/lib/usePagination";
import { cn } from "@/lib/utils";

/**
 * Documenti Mancanti Tab — compact operational dashboard
 * ------------------------------------------------------
 * 11/06/2026 — UI/UX refinement (su richiesta utente). Ridisegnato per
 * essere allineato al modulo "Note su Pratiche":
 *   • spacing e tipografia compatti (`text-xs`, padding ridotti)
 *   • singola riga per pratica con badge orizzontali per i documenti
 *   • i documenti mancanti diventano il focus visivo (badge ambra
 *     elencati per nome — niente più "N mancanti" generico)
 *   • progress bar inline più sottile
 *   • palette ridotta: ambra (missing) · verde (received) · blu (info)
 *     · slate (info secondarie)
 *   • filtro "Stato Documenti" default = "missing" (acceptance criterion:
 *     le pratiche complete non devono comparire nella vista Documenti
 *     Mancanti).
 *
 * Nessuna modifica a:
 *   • business logic (toggleDocument / addDocumentToCase /
 *     removeDocumentFromCase ricevuti come prop, comportamento immutato)
 *   • API, routing, permessi, filtri server-side
 *   • regole di completamento documenti
 */
function DocumentiTabInner({
  clients,
  cases,
  activeCases,
  searchQuery,
  setSearchQuery,
  filterClient,
  setFilterClient,
  filterDocumentStatus,
  setFilterDocumentStatus,
  toggleDocument,
  addDocumentToCase,
  removeDocumentFromCase,
  documentTemplates,
}) {
  // Per-case "add document" expanded state and input value
  const [expandedAddDoc, setExpandedAddDoc] = useState(null);
  const [newDocumentInputs, setNewDocumentInputs] = useState({});

  // Default filter = "missing" (acceptance criterion #9).
  // Apply once on mount: if the parent state is the bare default ("all"
  // or undefined), narrow it to "missing" — coerente con il nome del
  // modulo "Documenti Mancanti". L'operatore può sempre tornare a "all"
  // o "complete" dal filtro.
  useEffect(() => {
    if (!filterDocumentStatus || filterDocumentStatus === "all") {
      setFilterDocumentStatus("missing");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPI counts
  const casesWithMissingDocuments = useMemo(() => {
    return activeCases.filter((c) => {
      if (!c.requestedDocuments || c.requestedDocuments.length === 0)
        return false;
      return c.receivedDocuments.length < c.requestedDocuments.length;
    });
  }, [activeCases]);

  const casesWithCompleteDocuments = useMemo(() => {
    return activeCases.filter((c) => {
      if (!c.requestedDocuments || c.requestedDocuments.length === 0)
        return false;
      return c.receivedDocuments.length === c.requestedDocuments.length;
    });
  }, [activeCases]);

  // Filtered list (with current filters applied)
  const filteredDocumentCases = useMemo(() => {
    return activeCases.filter((caseItem) => {
      if (
        !caseItem.requestedDocuments ||
        caseItem.requestedDocuments.length === 0
      )
        return false;
      if (filterClient !== "all" && String(caseItem.clientId) !== filterClient)
        return false;
      if (filterDocumentStatus === "missing") {
        if (caseItem.receivedDocuments.length >= caseItem.requestedDocuments.length)
          return false;
      } else if (filterDocumentStatus === "complete") {
        if (caseItem.receivedDocuments.length < caseItem.requestedDocuments.length)
          return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const client = clients.find((c) => c.id === caseItem.clientId);
        if (
          !caseItem.caseNumber.toLowerCase().includes(query) &&
          !caseItem.subject.toLowerCase().includes(query) &&
          !caseItem.practiceArea.toLowerCase().includes(query) &&
          !client?.fullName.toLowerCase().includes(query)
        )
          return false;
      }
      return true;
    });
  }, [activeCases, filterClient, filterDocumentStatus, searchQuery, clients]);

  const {
    paged: pagedDocumentCases,
    paginationBar: documentCasesPaginationBar,
  } = usePagination(filteredDocumentCases, {
    storageKey: "documenti-pratiche",
    itemLabelSingular: "pratica",
    itemLabelPlural: "pratiche",
  });

  const setNewDocumentName = (caseId, value) =>
    setNewDocumentInputs((prev) => ({ ...prev, [caseId]: value }));

  return (
    <div className="space-y-3">
      {/* ─── KPI bar — compatta (3 KPI in singola riga) ─── */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                Documenti Mancanti
              </p>
              <p className="text-xl font-bold text-amber-600 leading-tight">
                {casesWithMissingDocuments.length}
              </p>
              <p className="text-[11px] text-slate-500">pratiche attive</p>
            </div>
            <AlertCircle className="h-5 w-5 text-amber-500" />
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-sm">
          <CardContent className="p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                Documentazione Completa
              </p>
              <p className="text-xl font-bold text-green-600 leading-tight">
                {casesWithCompleteDocuments.length}
              </p>
              <p className="text-[11px] text-slate-500">pratiche attive</p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                Totale Pratiche
              </p>
              <p className="text-xl font-bold text-blue-600 leading-tight">
                {
                  activeCases.filter((c) => c.requestedDocuments?.length > 0)
                    .length
                }
              </p>
              <p className="text-[11px] text-slate-500">con documenti richiesti</p>
            </div>
            <FileCheck className="h-5 w-5 text-blue-500" />
          </CardContent>
        </Card>
      </div>

      {/* ─── Filter bar — compatta (stesso pattern di Note su Pratiche) ─── */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Cerca pratica, cliente, tipo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 pl-8 text-xs"
            data-testid="search-documents-input"
          />
        </div>

        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger
            className="h-7 w-[150px] text-xs"
            data-testid="filter-doc-client-select"
          >
            <SelectValue placeholder="Cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">
              Tutti i clienti
            </SelectItem>
            {clients.map((client) => (
              <SelectItem
                key={client.id}
                value={String(client.id)}
                className="text-xs"
              >
                {client.fullName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filterDocumentStatus}
          onValueChange={setFilterDocumentStatus}
        >
          <SelectTrigger
            className={cn(
              "h-7 w-[170px] text-xs font-medium",
              filterDocumentStatus === "missing" &&
                "border-amber-400 bg-amber-50 text-amber-700",
              filterDocumentStatus === "complete" &&
                "border-green-400 bg-green-50 text-green-700"
            )}
            data-testid="filter-document-status-select"
          >
            <SelectValue placeholder="Stato Documenti" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="missing" className="text-xs">
              Solo mancanti
            </SelectItem>
            <SelectItem value="complete" className="text-xs">
              Solo complete
            </SelectItem>
            <SelectItem value="all" className="text-xs">
              Tutte
            </SelectItem>
          </SelectContent>
        </Select>

        {(filterClient !== "all" ||
          filterDocumentStatus !== "missing" ||
          searchQuery) && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-red-600 px-2"
            onClick={() => {
              setFilterClient("all");
              setFilterDocumentStatus("missing");
              setSearchQuery("");
            }}
          >
            <X className="h-3 w-3 mr-1" />
            Rimuovi filtri
          </Button>
        )}

        <div className="ml-auto text-[11px] text-slate-500 font-medium">
          {filteredDocumentCases.length}{" "}
          {filteredDocumentCases.length === 1 ? "pratica" : "pratiche"}
        </div>
      </div>

      {/* ─── Lista pratiche — singola riga compatta per ciascuna ─── */}
      <div className="space-y-1.5">
        {pagedDocumentCases.map((caseItem) => {
          const client = clients.find((c) => c.id === caseItem.clientId);
          const total = caseItem.requestedDocuments.length;
          const received = caseItem.receivedDocuments.length;
          const documentsProgress = total > 0 ? (received / total) * 100 : 0;
          const missingDocs = caseItem.requestedDocuments.filter(
            (doc) => !caseItem.receivedDocuments.includes(doc)
          );
          const isComplete = documentsProgress === 100;
          const isAddOpen = expandedAddDoc === caseItem.id;
          const newDocumentName = newDocumentInputs[caseItem.id] || "";

          return (
            <Card
              key={caseItem.id}
              className={cn(
                "border-l-4 hover:shadow-sm transition-shadow",
                isComplete ? "border-l-green-500" : "border-l-amber-500"
              )}
              data-testid={`doc-case-${caseItem.id}`}
            >
              <CardContent className="p-3">
                {/* Riga principale — info pratica + progress inline */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  {/* Sx: identificazione pratica */}
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge
                        variant="outline"
                        className="font-mono text-[10px] py-0 px-1.5 h-5"
                      >
                        {caseItem.caseNumber}
                      </Badge>
                      <Badge
                        className={cn(
                          "text-[10px] py-0 px-1.5 h-5",
                          statusConfig[caseItem.status]?.color
                        )}
                      >
                        {caseItem.status}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0 px-1.5 h-5 bg-slate-50 text-slate-600"
                      >
                        {caseItem.practiceArea}
                      </Badge>
                      {isComplete ? (
                        <Badge className="text-[10px] py-0 px-1.5 h-5 bg-green-100 text-green-700 border-green-300">
                          <CheckCircle2 className="h-3 w-3 mr-0.5" />
                          Completa
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] py-0 px-1.5 h-5 bg-amber-100 text-amber-800 border-amber-300">
                          <AlertCircle className="h-3 w-3 mr-0.5" />
                          {missingDocs.length}/{total} mancanti
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-800 leading-tight">
                      {caseItem.subject}
                    </div>
                    <div className="text-xs text-slate-500 leading-tight">
                      Cliente:{" "}
                      <span className="font-medium text-slate-700">
                        {client?.fullName || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Dx: progress compatto */}
                  <div className="flex flex-col items-end gap-1 min-w-[160px]">
                    <div className="text-[11px] text-slate-600">
                      <span className="font-semibold text-slate-700">
                        {received}/{total}
                      </span>{" "}
                      documenti{" "}
                      <span className="text-slate-500">
                        ({Math.round(documentsProgress)}%)
                      </span>
                    </div>
                    <div className="h-1.5 w-40 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full transition-all",
                          isComplete ? "bg-green-500" : "bg-amber-500"
                        )}
                        style={{ width: `${documentsProgress}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Badge dei documenti mancanti — visivamente prioritari */}
                {missingDocs.length > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">
                      Da ricevere:
                    </span>
                    {missingDocs.map((doc) => (
                      <Badge
                        key={doc}
                        variant="outline"
                        className="group text-[11px] py-0 px-1.5 h-5 bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer"
                        onClick={() => toggleDocument(caseItem.id, doc)}
                        title="Clicca per segnare come ricevuto"
                        data-testid={`doc-missing-badge-${caseItem.id}-${doc}`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mr-1" />
                        {doc}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeDocumentFromCase(caseItem.id, doc);
                          }}
                          className="ml-1 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                          aria-label="Rimuovi documento dalla pratica"
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Badge documenti ricevuti — denso, sola riga */}
                {received > 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-semibold text-green-700 uppercase tracking-wide">
                      Ricevuti:
                    </span>
                    {caseItem.receivedDocuments.map((doc) => (
                      <Badge
                        key={doc}
                        className="group text-[11px] py-0 px-1.5 h-5 bg-green-100 text-green-800 border border-green-300 hover:bg-green-200 cursor-pointer"
                        onClick={() => toggleDocument(caseItem.id, doc)}
                        title="Clicca per segnare come non ricevuto"
                        data-testid={`doc-received-badge-${caseItem.id}-${doc}`}
                      >
                        <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                        {doc}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeDocumentFromCase(caseItem.id, doc);
                          }}
                          className="ml-1 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                          aria-label="Rimuovi documento dalla pratica"
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Toggle: aggiunta documento — collassato di default */}
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-[11px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                    onClick={() =>
                      setExpandedAddDoc(isAddOpen ? null : caseItem.id)
                    }
                    data-testid={`toggle-add-doc-${caseItem.id}`}
                  >
                    {isAddOpen ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                    Aggiungi documento
                  </button>

                  {isAddOpen && (
                    <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 space-y-2">
                      <div className="flex gap-1.5">
                        <Input
                          placeholder="Nome documento..."
                          value={newDocumentName}
                          onChange={(e) =>
                            setNewDocumentName(caseItem.id, e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && newDocumentName.trim()) {
                              addDocumentToCase(caseItem.id, newDocumentName);
                              setNewDocumentName(caseItem.id, "");
                            }
                          }}
                          className="h-7 text-xs flex-1"
                          data-testid={`add-doc-input-${caseItem.id}`}
                        />
                        <Button
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => {
                            if (newDocumentName.trim()) {
                              addDocumentToCase(caseItem.id, newDocumentName);
                              setNewDocumentName(caseItem.id, "");
                            }
                          }}
                          data-testid={`add-doc-button-${caseItem.id}`}
                        >
                          <PlusCircle className="h-3 w-3 mr-1" />
                          Aggiungi
                        </Button>
                      </div>

                      {documentTemplates[caseItem.practiceArea] && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] text-slate-500">
                            Template {caseItem.practiceArea}:
                          </span>
                          {documentTemplates[caseItem.practiceArea]
                            .filter(
                              (doc) =>
                                !caseItem.requestedDocuments.includes(doc)
                            )
                            .map((doc) => (
                              <Badge
                                key={doc}
                                variant="secondary"
                                className="cursor-pointer hover:bg-blue-100 text-[11px] py-0 px-1.5 h-5"
                                onClick={() =>
                                  addDocumentToCase(caseItem.id, doc)
                                }
                              >
                                <Plus className="h-2.5 w-2.5 mr-0.5" />
                                {doc}
                              </Badge>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Empty state */}
        {filteredDocumentCases.length === 0 && (
          <Card className="shadow-sm">
            <CardContent className="text-center py-10">
              <FileCheck className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                {filterDocumentStatus === "missing"
                  ? "Nessuna pratica con documenti mancanti — tutto a posto!"
                  : filterDocumentStatus === "complete"
                  ? "Nessuna pratica con documentazione completa al momento."
                  : "Nessuna pratica con documenti richiesti."}
              </p>
              {filterDocumentStatus === "missing" &&
                casesWithCompleteDocuments.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    {casesWithCompleteDocuments.length} pratiche con documenti
                    completi — usa il filtro &quot;Solo complete&quot; per consultarle.
                  </p>
                )}
            </CardContent>
          </Card>
        )}
        {documentCasesPaginationBar}
      </div>
    </div>
  );
}

export const DocumentiTab = React.memo(DocumentiTabInner);
